/**
 * Agent Components Configuration
 * Maps agent IDs to their frontend routes
 */

// Agent ID → Routes mapping
const agentComponentsMap = {
  '13': {  // Bank Statement Agent
    components: [
      {
        name: 'Upload',
        displayName: 'Upload Bank Statement',
        description: 'Upload and process bank statements with AI',
        icon: 'upload',
        type: 'workflow',
        workflowId: 16
      },
      {
        name: 'History',
        displayName: 'Bank Statement History',
        description: 'View previously processed bank statements',
        icon: 'clock',
        type: 'page',
        route: '/bank-chat-history'
      },
      {
        name: 'ImpactDashboard',
        displayName: 'Impact Dashboard',
        description: 'See time saved, money saved, and ROI delivered by this agent',
        icon: 'chart-line',
        type: 'page',
        route: '/impact-dashboard/13'
      },
      {
        name: 'SpendAnalysis',
        displayName: 'Spend Analysis',
        description: 'View API costs and token usage per workflow run',
        icon: 'dollar-sign',
        type: 'page',
        route: '/spend-analysis/13',
        adminOnly: true
      }
    ]
  },
  '3': {   // Bill Processing Agent
    components: [
      {
        name: 'Upload',
        displayName: 'Upload Bill',
        description: 'Upload and process vendor bills/invoices',
        icon: 'upload',
        type: 'workflow',
        workflowId: 14,
        route: '/workflows/14/chat'
      },
      {
        name: 'BillHistory',
        displayName: 'Bill Info',
        description: 'View all processed bills with vendor and NetSuite details',
        icon: 'file-invoice',
        type: 'page',
        route: '/bill-info',
        adminOnly: true
      },
      {
        name: 'ImpactDashboard',
        displayName: 'Impact Dashboard',
        description: 'See time saved, money saved, and ROI delivered by this agent',
        icon: 'chart-line',
        type: 'page',
        route: '/impact-dashboard/3'
      },
      {
        name: 'SpendAnalysis',
        displayName: 'Spend Analysis',
        description: 'View API costs and token usage per workflow run',
        icon: 'dollar-sign',
        type: 'page',
        route: '/spend-analysis/3',
        adminOnly: true
      },
      {
        name: 'History',
        displayName: 'Bill Processing History',
        description: 'View previously processed bills in chat threads',
        icon: 'clock',
        type: 'page',
        route: '/threads'
      }
    ]
  },
  '12': {  // Sales Order Agent (NetSuite)
    components: [
      {
        name: 'Upload',
        displayName: 'Upload Sales Order',
        description: 'Upload and process sales orders with AI',
        icon: 'upload',
        type: 'workflow',
        workflowId: 15,
        chatRoute: '/sale-order-agent'  // Dedicated chat page for NetSuite workflow
      },
      {
        name: 'History',
        displayName: 'Sales Order History',
        description: 'View previously processed sales orders',
        icon: 'clock',
        type: 'page',
        route: '/sale-order-history'
      },
      {
        name: 'ImpactDashboard',
        displayName: 'Impact Dashboard',
        description: 'See time saved, money saved, and ROI delivered by this agent',
        icon: 'chart-line',
        type: 'page',
        route: '/impact-dashboard/12'
      },
      {
        name: 'SpendAnalysis',
        displayName: 'Spend Analysis',
        description: 'View API costs and token usage per workflow run',
        icon: 'dollar-sign',
        type: 'page',
        route: '/spend-analysis/12',
        adminOnly: true
      }
    ]
  },
  '15': {  // Sales Order Agent (Salesforce)
    components: [
      {
        name: 'Upload',
        displayName: 'Upload Purchase Order',
        description: 'Upload PO and create Salesforce Sales Order',
        icon: 'upload',
        type: 'workflow',
        workflowId: 21,
        chatRoute: '/sale-order-sf'  // Dedicated chat page for SF workflow
      },
      {
        name: 'History',
        displayName: 'SF Sales Order History',
        description: 'View previously processed Salesforce sales orders',
        icon: 'clock',
        type: 'page',
        route: '/sale-order-sf-history'
      },
      {
        name: 'ImpactDashboard',
        displayName: 'Impact Dashboard',
        description: 'See time saved, money saved, and ROI delivered by this agent',
        icon: 'chart-line',
        type: 'page',
        route: '/impact-dashboard/15'
      },
      {
        name: 'SpendAnalysis',
        displayName: 'Spend Analysis',
        description: 'View API costs and token usage per workflow run',
        icon: 'dollar-sign',
        type: 'page',
        route: '/spend-analysis/15',
        adminOnly: true
      }
    ]
  },
  '19': {  // RFQ Workflow Agent
    components: [
      {
        name: 'Dashboard',
        displayName: 'RFQ Dashboard',
        description: 'View and manage RFQ workflows with AI-powered vendor communication',
        icon: 'clipboard-list',
        type: 'page',
        route: '/rfq-workflow'
      },
      {
        name: 'NewRFQ',
        displayName: 'New RFQ',
        description: 'Start a new RFQ workflow with automated vendor outreach',
        icon: 'plus-circle',
        type: 'workflow',
        workflowId: 25,
        route: '/rfq-workflow'
      },
      {
        name: 'SpendAnalysis',
        displayName: 'Spend Analysis',
        description: 'View API costs and token usage for RFQ workflows',
        icon: 'dollar-sign',
        type: 'page',
        route: '/spend-analysis/19',
        adminOnly: true
      }
    ]
  }
};

/**
 * Get components for a specific agent
 * @param {string} agentId - The agent ID
 * @returns {Array} - Array of component configs or empty array
 */
export const getAgentComponents = (agentId) => {
  const agentConfig = agentComponentsMap[String(agentId)];
  return agentConfig ? agentConfig.components : [];
};

/**
 * Check if agent has frontend components
 * @param {string} agentId - The agent ID
 * @returns {boolean}
 */
export const hasAgentComponents = (agentId) => {
  return !!agentComponentsMap[String(agentId)];
};

export default agentComponentsMap;
